import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  User, Mail, Lock, Eye, EyeOff, ArrowRight, UserPlus, 
  LogIn, Phone, CheckCircle, AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const theme = {
  bg: '#F7F4EF',
  orange: '#E8621A',
  ink: '#1A1713',
  amber: '#B8760A',
  blue: '#1A4A8A',
  green: '#1D7A4F',
  red: '#C0392B',
};

const TeamAuthPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' or 'signup'
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'setter'
  });

  // Check if already logged in
  useEffect(() => {
    const member = localStorage.getItem('crm_member');
    if (member) {
      navigate('/crm');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'signup') {
        // Create account
        const res = await fetch(`${API_URL}/api/team/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        const data = await res.json();
        
        if (data.error) {
          setError(data.error);
        } else if (data.member) {
          setSuccess('Compte créé ! Vous pouvez maintenant vous connecter.');
          setMode('login');
          setFormData({ ...formData, password: '' });
        }
      } else {
        // Login
        const res = await fetch(`${API_URL}/api/team/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password
          })
        });
        const data = await res.json();
        
        if (data.error) {
          setError(data.error);
        } else if (data.member) {
          // Store member info — keep auth version in sync with CRMPage
          localStorage.setItem('crm_member', JSON.stringify(data.member));
          localStorage.setItem('crm_unlocked', 'true');
          localStorage.setItem('crm_auth_version', '2026-02-20-v2');
          navigate('/crm');
        }
      }
    } catch (err) {
      setError('Erreur de connexion. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: theme.bg }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-bebas text-4xl mb-2" style={{ color: theme.ink }}>
            PRSNL <span style={{ color: theme.orange }}>CRM</span>
          </h1>
          <p className="text-gray-500">
            {mode === 'login' ? 'Connectez-vous à votre espace' : 'Créez votre compte équipe'}
          </p>
        </div>

        {/* Toggle */}
        <div className="flex bg-white rounded-xl p-1 mb-6 border border-gray-200">
          <button
            onClick={() => setMode('login')}
            className={`flex-1 py-3 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${
              mode === 'login' 
                ? 'bg-orange-500 text-white' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <LogIn className="w-4 h-4" />
            Connexion
          </button>
          <button
            onClick={() => setMode('signup')}
            className={`flex-1 py-3 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${
              mode === 'signup' 
                ? 'bg-orange-500 text-white' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <UserPlus className="w-4 h-4" />
            Inscription
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
          {/* Error/Success messages */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2 text-green-700 text-sm">
              <CheckCircle className="w-4 h-4" />
              {success}
            </div>
          )}

          {/* Name (signup only) */}
          {mode === 'signup' && (
            <div className="mb-4">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">
                Prénom
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Mathis"
                  required={mode === 'signup'}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-orange-400 transition-colors"
                />
              </div>
            </div>
          )}

          {/* Email */}
          <div className="mb-4">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="mathis@prsnl.fr"
                required
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-orange-400 transition-colors"
              />
            </div>
          </div>

          {/* Password */}
          <div className="mb-4">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">
              Mot de passe
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                required
                minLength={4}
                className="w-full pl-10 pr-12 py-3 rounded-xl border border-gray-200 outline-none focus:border-orange-400 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Phone (signup only) */}
          {mode === 'signup' && (
            <div className="mb-4">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">
                Téléphone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="06 12 34 56 78"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-orange-400 transition-colors"
                />
              </div>
            </div>
          )}

          {/* Role (signup only) */}
          {mode === 'signup' && (
            <div className="mb-6">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">
                Rôle
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'setter' })}
                  className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${
                    formData.role === 'setter' 
                      ? 'bg-amber-500 text-white' 
                      : 'bg-amber-50 text-amber-700 border border-amber-200'
                  }`}
                >
                  📞 Setter
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'closer' })}
                  className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${
                    formData.role === 'closer' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-blue-50 text-blue-700 border border-blue-200'
                  }`}
                >
                  🎯 Closer
                </button>
              </div>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: theme.orange }}
          >
            {loading ? (
              'Chargement...'
            ) : mode === 'login' ? (
              <>
                Se connecter
                <ArrowRight className="w-5 h-5" />
              </>
            ) : (
              <>
                Créer mon compte
                <UserPlus className="w-5 h-5" />
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center text-gray-400 text-sm mt-6">
          {mode === 'login' ? (
            <>Pas encore de compte ? <button onClick={() => setMode('signup')} className="text-orange-500 font-bold">Inscrivez-vous</button></>
          ) : (
            <>Déjà un compte ? <button onClick={() => setMode('login')} className="text-orange-500 font-bold">Connectez-vous</button></>
          )}
        </p>
      </motion.div>
    </div>
  );
};

export default TeamAuthPage;
